import { type FieldFunctionOptions } from "@apollo/client"
import { type KeyArgsFunction } from "@apollo/client/cache/inmemory/policies"
// oxlint-disable-next-line no-restricted-imports
import {
  type AuditFactDetail,
  type AuditFactPage,
  type QueryListAuditFactsArgs,
  type QueryListAuditFactsByObjectArgs,
  type QueryReadAuditFactArgs,
} from "@digits-graphql/admin/graphql-bearer"
import { allKeyArgs } from "src/shared/initializers/typePolicies/helpers/allKeyArgs"

// The page anchor (statedBefore) is out of the cache key so that a later
// page's anchored write lands on the field the unanchored first page holds.
const PAGE_EXCLUDED_KEYS = ["pagination", "statedBefore"]

const pageKeyArgs: KeyArgsFunction = (args, context) =>
  args ? allKeyArgs(args, PAGE_EXCLUDED_KEYS) : context.fieldName

type PageArgs = QueryListAuditFactsArgs | QueryListAuditFactsByObjectArgs

// An AuditFactPage is an object, not a list, so the shared offsetLimitPagination
// cannot merge it. Later pages append their facts and take the flags they
// carry; the strip, the anchor, and the actor options come from the first page
// only and survive a load-more.
export function auditFactPagePagination() {
  return {
    keyArgs: pageKeyArgs,
    merge(
      existing: AuditFactPage | undefined,
      incoming: AuditFactPage,
      { args }: FieldFunctionOptions<PageArgs>
    ): AuditFactPage {
      if (!existing || (args?.pagination.offset ?? 0) === 0) {
        return incoming
      }
      const firstPageCarriesOptions = incoming.actorOptions.length > 0
      return {
        ...incoming,
        facts: [...existing.facts, ...incoming.facts],
        pageAnchor: existing.pageAnchor ?? incoming.pageAnchor,
        pageAnchorPrecise: existing.pageAnchorPrecise ?? incoming.pageAnchorPrecise,
        status: incoming.status ?? existing.status,
        actorOptions: firstPageCarriesOptions ? incoming.actorOptions : existing.actorOptions,
        actorOptionsTruncated: firstPageCarriesOptions
          ? incoming.actorOptionsTruncated
          : existing.actorOptionsTruncated,
      }
    },
  }
}

// The detail's objects page applies to every version at once. A later page
// appends each version's objects onto the same version already held, matched
// by interpreter version and not by position, so a version that lands between
// two pages takes no other version's objects. The fact sits inline (AuditFact
// keeps keyFields false) and the fact selection carries interpreterVersion, so
// the version reads directly.
export function auditFactDetailObjectsPagination() {
  return {
    keyArgs: ["legalEntityId", "id"],
    merge(
      existing: AuditFactDetail | undefined,
      incoming: AuditFactDetail,
      { args }: FieldFunctionOptions<QueryReadAuditFactArgs>
    ): AuditFactDetail {
      if (!existing || (args?.objectsPagination?.offset ?? 0) === 0) {
        return incoming
      }
      const held = new Map(
        existing.versions.map((version) => [version.fact.interpreterVersion, version.objects])
      )
      return {
        ...incoming,
        versions: incoming.versions.map((version) => ({
          ...version,
          objects: [...(held.get(version.fact.interpreterVersion) ?? []), ...version.objects],
        })),
      }
    },
  }
}
